/**
 * Zoom state for one file surface: the header's +/− buttons, Ctrl/⌘ with
 * `=`, `-` and `0` while focus is inside the surface, and Ctrl+wheel or a
 * trackpad pinch over it. The value resets whenever `resetKey` changes, so
 * opening another file starts at 100%.
 *
 * How the value is applied is the surface's business: CSS `zoom` on a
 * reflowing body, the PDF viewer's `zoom=` fragment, or the presentation's
 * fit-to-width multiplier.
 *
 * @module components/files/useSurfaceZoom
 */
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";

import {
  clampPreviewZoom,
  PREVIEW_ZOOM_MIN,
  formatPreviewZoom,
  previewZoomShortcut,
  wheelZoomFactor,
  zoomInStep,
  zoomOutStep,
} from "./previewZoom";

export interface SurfaceZoom {
  readonly zoom: number;
  readonly label: string;
  readonly zoomIn: () => void;
  readonly zoomOut: () => void;
  readonly reset: () => void;
  /** Attach to the element the wheel gesture should act over. */
  readonly viewportRef: (element: HTMLElement | null) => void;
  /** Attach to the same element (or an ancestor) so the chords work while focus is inside it. */
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

export function useSurfaceZoom(
  resetKey: unknown,
  options: { readonly min?: number } = {},
): SurfaceZoom {
  const min = options.min ?? PREVIEW_ZOOM_MIN;
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const change = useCallback(
    (next: number) => {
      const value = clampPreviewZoom(next, min);
      zoomRef.current = value;
      setZoom(value);
    },
    [min],
  );

  useEffect(() => {
    change(1);
  }, [change, resetKey]);

  const detachRef = useRef<(() => void) | null>(null);
  const viewportRef = useCallback(
    (element: HTMLElement | null) => {
      detachRef.current?.();
      detachRef.current = null;
      if (!element) return;
      const wheel = (event: WheelEvent) => {
        // Plain wheel scrolls; only the zoom gesture (Ctrl+wheel, or a pinch,
        // which browsers report as a wheel with ctrlKey) is claimed, and
        // claimed fully so the page itself does not zoom.
        if (!(event.ctrlKey || event.metaKey) || event.deltaY === 0) return;
        event.preventDefault();
        change(
          zoomRef.current * wheelZoomFactor(event.deltaY, event.deltaMode, element.clientHeight),
        );
      };
      element.addEventListener("wheel", wheel, { passive: false });
      detachRef.current = () => element.removeEventListener("wheel", wheel);
    },
    [change],
  );
  useEffect(() => () => detachRef.current?.(), []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const action = previewZoomShortcut(event);
      if (!action) return;
      event.preventDefault();
      change(
        action === "in"
          ? zoomInStep(zoomRef.current, min)
          : action === "out"
            ? zoomOutStep(zoomRef.current, min)
            : 1,
      );
    },
    [change, min],
  );

  return {
    zoom,
    label: formatPreviewZoom(zoom),
    zoomIn: useCallback(() => change(zoomInStep(zoomRef.current, min)), [change, min]),
    zoomOut: useCallback(() => change(zoomOutStep(zoomRef.current, min)), [change, min]),
    reset: useCallback(() => change(1), [change]),
    viewportRef,
    onKeyDown,
  };
}
